import { CreditType, QuotationState, QuoteDetailsFragment } from 'src/api/api.generated'
import { v4 as uuid } from 'uuid'
import { getDayDiff, getNightsInRange } from 'src/utils/date'
import { ReservationCartRow } from '../booking/reservation-insert/form/reservation-new.type'

const QuoteSessionStorageKey = 'QUOTE'

type ReservationCartRowStorage = Omit<ReservationCartRow, 'start' | 'end'> & {
  start: number
  end: number
}

type QuoteSessionStorage = {
  checkIn: Date | null
  checkOut: Date | null
  adults: number
  children: { childId: string; quantity: number }[]
  customerCompanyId: string | null
  credit: CreditType
  customerPersonId: string | undefined
  rows: ReservationCartRowStorage[]
}

export function storeQuoteInSessionStorage(
  checkIn: Date | null,
  checkOut: Date | null,
  adults: number,
  children: { childId: string; quantity: number }[],
  customerCompanyId: string | null,
  customerPersonId: string | undefined,
  rows: ReservationCartRow[],
  credit: CreditType,
) {
  sessionStorage.setItem(
    QuoteSessionStorageKey,
    JSON.stringify({
      checkIn: checkIn,
      checkOut: checkOut,
      adults: adults,
      children: children,
      customerCompanyId: customerCompanyId,
      customerPersonId: customerPersonId,
      credit: credit,
      rows: rows.map((row) => {
        return {
          ...row,
          start: row.start.getTime(),
          end: row.end.getTime(),
        }
      }),
    } as QuoteSessionStorage),
  )
}

export function readQuoteInSessionStorage(): QuoteSessionStorage | null {
  const quoteJson = sessionStorage.getItem(QuoteSessionStorageKey)
  if (quoteJson) {
    return JSON.parse(quoteJson) as QuoteSessionStorage
  } else {
    return null
  }
}

export function removeQuoteInSessionStorage() {
  sessionStorage.removeItem(QuoteSessionStorageKey)
}
export function quotationIsEditable(state: QuotationState | undefined): boolean {
  return state !== 'CANCELED' && state !== 'CONFIRMED' && state !== 'EXPIRED' && state !== undefined
}

export function convertQuoteDetailsFragmentToReservationCartRows(quotationDetails: QuoteDetailsFragment): { id: string; rows: ReservationCartRow[] }[] {
  return quotationDetails.quotationDetails.map((quote, index) => {
    const rows: ReservationCartRow[] = quote.quotedResources
      .filter((res) => res.resourceOccupancies.length > 0)
      .map((resource) => {
        const resourceOccupancies = resource.resourceOccupancies[0]
        const manualDailyPrices = resource.manualDailyPrices
        const price = manualDailyPrices ? manualDailyPrices.map((res) => res.price).reduce((acc, val) => acc + val) / manualDailyPrices.length : resource.manualPrice ? resource.manualPrice : 0
        const days = getDayDiff(resource.start, resource.end)
        return {
          id: uuid(),
          unavailable: false,
          isFirstInsertion: index === 1,
          isSummary: false,
          rate: {
            id: resource.rateId,
            name: resource.rate.name,
          },
          chargeCategory: {
            id: resource.chargeCategory.id,
            name: resource.chargeCategory.name,
          },
          occupancy: {
            adults: resource.adults,
            children:
              resource.childOccupancies?.map((co) => {
                return {
                  quantity: co.quantity,
                  childId: co.childId,
                }
              }) ?? [],
          },
          initialPrice: price,
          averagePrice: price,
          initialTotalPrice: days * price,
          totalPrice: days * price,
          initialDailyPrices:
            resource.manualDailyPrices ??
            getNightsInRange(resource.start, resource.end).map((night) => {
              return {
                day: night,
                price: price,
              }
            }),
          nights: days,
          start: resource.start,
          end: resource.end,
          resourceType: {
            id: resourceOccupancies.resourceTypeId,
            name: resourceOccupancies.resourceType.customName ?? resourceOccupancies.resourceType.description,
            code: resourceOccupancies.resourceType.code ?? undefined,
          },
          policy: undefined,
          addedAdditionalServices:
            resource.additionalServices
              // ?.filter((as) => !as.priceZero)
              ?.map((as) => {
                return {
                  id: as.additionalService.id,
                  priceZero: as.priceZero,
                  adults: resource.adults,
                  children: resource.children,
                  nights: getDayDiff(resource.start, resource.end),
                  price: as.additionalService.price,
                  quantity: as.quantity,
                  availability: undefined,
                  added: true,
                  name: as.additionalService.name,
                  description: as.additionalService.description,
                  currency: as.additionalService.currency,
                  pricingType: as.additionalService.pricingType,
                  automaticForAdults: as.additionalService.automaticForAdults,
                  automaticForChildren: as.additionalService.automaticForChildren,
                  childPrices: as.additionalService.childServicePrices,
                  minStay: as.additionalService.minStay ?? 1,
                }
              }) ?? undefined,
          includedAdditionalServices: resource.rate.mandatoryAdditionalServices
            // ?.filter((as) => as.included)
            ?.map((additionalService) => {
              return {
                id: additionalService.additionalService.id,
                name: additionalService.additionalService.name,
                description: additionalService.additionalService.description,
                price: additionalService.additionalService.price,
                added: false,
                priceZero: additionalService.priceZero,
                pricingType: additionalService.additionalService.pricingType,
                adults: resource.adults,
                children: resource.children,
                nights: days,
                currency: additionalService.additionalService.currency,
                automaticForAdults: additionalService.additionalService.automaticForAdults,
                automaticForChildren: additionalService.additionalService.automaticForChildren,
                childPrices: additionalService.additionalService.childServicePrices,
                minStay: additionalService.additionalService.minStay ?? 1,
              }
            }),
        }
      })
    return {
      id: uuid(),
      rows: rows,
    }
  })
}
